import React, { createContext, useContext, useState, useEffect } from 'react';
import { projectsAPI } from '../api/api';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Projeleri API'den yükle
  const loadProjects = async (departmentId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // console.log('🚀 ProjectContext - Loading projects...', { departmentId });
      const response = await projectsAPI.getProjects(departmentId);
      // console.log('📨 ProjectContext - API Response:', response);
      
      if (response.success) {
        const validProjects = (response.data || []).filter(p => p && p.id);
        // console.log('✅ ProjectContext - Valid projects:', validProjects);
        // console.log('📊 ProjectContext - Projects count:', validProjects.length);
        
        // Geçici debug - Global scope'a projects'i ekle
        window.debugProjects = validProjects;
        // console.log('🔍 Debug: Projects global değişkene atandı. Console\'da "window.debugProjects" yazarak erişebilirsiniz.');
        
        setProjects(validProjects);
        
        // Projeler yüklendikten sonra localStorage'dan seçili projeyi oku
        const savedProjectId = localStorage.getItem('selectedProjectId');
        if (savedProjectId && validProjects.length > 0) {
          const project = validProjects.find(p => p && p.id === parseInt(savedProjectId));
          if (project) {
            setSelectedProject(project);
          } else {
            // Varsayılan olarak ilk projeyi seç
            setSelectedProject(validProjects[0]);
          }
        } else if (validProjects.length > 0) {
          // Varsayılan olarak ilk projeyi seç
          setSelectedProject(validProjects[0]);
        }
      } else {
        setError('Projeler yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      setError('Projeler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Authentication durumu değiştiğinde projeleri yükle
  useEffect(() => {
    if (isAuthenticated) {
      // console.log('🔍 User authenticated, loading projects...');
      loadProjects();
    } else {
      // console.log('🔍 User not authenticated, clearing projects...');
      setProjects([]);
      setSelectedProject(null);
      setLoading(false);
    }
  }, [isAuthenticated]);


  // Proje değiştiğinde localStorage'a kaydet
  const selectProject = (project) => {
    if (project && project.id) {
      setSelectedProject(project);
      localStorage.setItem('selectedProjectId', project.id.toString());
    }
  };

  const updateProject = (updatedProject) => {
    if (!updatedProject || !updatedProject.id) {
      return;
    }
    
    // Projects listesini güncelle
    setProjects(prevProjects => 
      (prevProjects || []).filter(p => p && p.id).map(p => 
        p.id === updatedProject.id ? updatedProject : p
      )
    );
    
    // Eğer güncellenen proje seçili projeyse onu da güncelle
    if (selectedProject?.id === updatedProject.id) {
      setSelectedProject(updatedProject);
    }
  };

  // Departmana göre projeleri yükle
  const loadProjectsByDepartment = async (departmentId, hotelId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectsAPI.getProjects(departmentId, hotelId);
      
      if (response && response.success) {
        const validProjects = (response.data || []).filter(p => p && p.id);
        setProjects(validProjects);
        setSelectedProject(null);
      } else {
        setError('Projeler yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      setError('Projeler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };
  
  const value = {
    projects,
    selectedProject,
    setSelectedProject,
    selectProject,
    updateProject,
    setProjects,
    loading,
    error,
    loadProjects,
    loadProjectsByDepartment,
    reloadProjects: loadProjects
  };
  
  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;